import { z } from "zod";
import { canvasVersionChecksSchema } from "./canvas.js";
/*
 * Artifact writes a running turn makes through the tenant API. Each request carries the run's
 * lease; the tenant accepts it only while that attempt holds the run, so an attempt that lost its
 * lease can no longer write.
 */
const id = z.string().trim().min(1).max(200);
const lease = {
    runId: id, tenantSlug: id, workerId: id,
    attemptNumber: z.number().int().positive(),
    sessionId: id.optional(),
};
const toolCallId = z.string().trim().min(1).max(500);
const title = z.string().trim().min(1).max(500);
const filename = z.string().trim().min(1).max(200);
const provenance = z.record(z.string(), z.unknown()).optional();
export const workerArtifactRequestSchema = z.discriminatedUnion("operation", [
    z.object({ ...lease, operation: z.literal("create"), input: z.object({
            title, kind: z.enum(["csv", "markdown", "html"]), content: z.string(), filename,
            mimeType: z.enum(["text/csv", "text/markdown", "text/html"]), sourceToolName: z.string().trim().min(1).max(200), toolCallId,
            provenance,
        }).strict() }).strict(),
    z.object({ ...lease, operation: z.literal("update_csv"), input: z.object({
            artifactId: id, csv: z.string(), title: title.optional(), filename: filename.optional(),
            baseVersionId: id.optional(), toolCallId,
        }).strict() }).strict(),
    z.object({ ...lease, operation: z.literal("propose_markdown_revision"), input: z.object({
            artifactId: id, markdown: z.string(), title: title.optional(), baseVersionId: id.optional(), toolCallId,
        }).strict() }).strict(),
    z.object({ ...lease, operation: z.literal("previous_response"), input: z.object({}).strict() }).strict(),
    // A canvas revision: a new accepted version of an HTML artifact of the run's own task.
    z.object({ ...lease, operation: z.literal("update_html"), input: z.object({
            artifactId: id, html: z.string(), title: title.optional(), baseVersionId: id.optional(), toolCallId,
            sourceToolName: z.string().trim().min(1).max(200), provenance,
        }).strict() }).strict(),
    // A canvas of the run's own task and the annotations still open on it, to revise.
    z.object({ ...lease, operation: z.literal("canvas_source"), input: z.object({
            artifactId: id,
        }).strict() }).strict(),
    // What the automatic checks found on a version of a canvas of the run's own task, if recorded.
    z.object({ ...lease, operation: z.literal("canvas_checks"), input: z.object({
            artifactId: id, versionId: id,
        }).strict() }).strict(),
    // Records the checks on a version this run wrote.
    z.object({ ...lease, operation: z.literal("record_canvas_checks"), input: z.object({
            artifactId: id, versionId: id, checks: canvasVersionChecksSchema,
        }).strict() }).strict(),
]);
const stored = {
    artifactId: id, versionId: id, version: z.number().int().positive(), title: z.string(),
};
const file = {
    filename: z.string(), mimeType: z.enum(["text/csv", "text/markdown", "text/html"]),
    byteLength: z.number().int().nonnegative(), sha256: z.string().regex(/^[a-f0-9]{64}$/u),
};
export const workerArtifactResponseSchema = z.discriminatedUnion("operation", [
    z.object({ operation: z.literal("create"), artifact: z.object({ ...stored, ...file }).strict() }).strict(),
    z.object({ operation: z.literal("update_csv"), artifact: z.object({ ...stored, ...file }).strict() }).strict(),
    z.object({ operation: z.literal("propose_markdown_revision"), artifact: z.object(stored).strict() }).strict(),
    z.object({ operation: z.literal("previous_response"), previous: z.object({
            sourceRunId: id, sourceContentField: z.literal("task_runs.response_text"), markdown: z.string(),
        }).strict() }).strict(),
    z.object({ operation: z.literal("update_html"), artifact: z.object({ ...stored, ...file }).strict() }).strict(),
    z.object({ operation: z.literal("canvas_source"), canvas: z.object({
            ...stored, html: z.string(),
            annotations: z.array(z.object({
                id, anchorId: z.string(), anchorLabel: z.string(), body: z.string(),
                version: z.number().int().positive(),
            }).strict()),
        }).strict() }).strict(),
    z.object({ operation: z.literal("canvas_checks"), checks: canvasVersionChecksSchema.nullable() }).strict(),
    z.object({ operation: z.literal("record_canvas_checks"), recorded: z.literal(true) }).strict(),
]);
/** Stable refusal codes; the executor turns each into the tool error the agent sees. */
export const WORKER_ARTIFACT_REFUSALS = {
    leaseNotHeld: "artifact_lease_not_held",
    artifactNotFound: "artifact_not_found",
    versionConflict: "artifact_version_conflict",
    previousTurnMissing: "previous_turn_missing",
    previousResponseMissing: "previous_response_missing",
    contentTooLarge: "artifact_content_too_large",
};
/** A download name made from a title or a requested filename, with the artifact's extension. */
export function artifactFilename(value, extension) {
    const base = value
        .trim()
        .toLowerCase()
        .replace(new RegExp(`\\.${extension}$`, "u"), "")
        .replace(/[^a-z0-9]+/gu, "-")
        .replace(/^-+|-+$/gu, "")
        .slice(0, 80);
    return `${base || "artifact"}.${extension}`;
}
